/**
 * Estados operativos del regulador y su representación visual en el mapa.
 *
 * PROVISIONAL — paleta propuesta a ARS93, sujeta a revisión.
 *
 * El payload MQTT trae `estado` como string libre; algunos valores son alias
 * de "activo" (vocabulario heredado de Proto B/M). `resolveEstado` normaliza.
 */

export interface EstadoVisual {
  /** Clave canónica del estado. */
  key: string;
  /** Etiqueta para mostrar al usuario. */
  label: string;
  /** Color del marker (hex). */
  color: string;
  /** Halo del marker (rgba con alpha). */
  glow: string;
  /** Si el marker debe pulsar (estados que requieren atención). */
  pulse: boolean;
}

// Estados cuyo nombre en el payload equivale a "regulando con ciclo".
const ALIAS_ACTIVO = new Set(['activo', 'colores', 'horario', 'demanda']);

const ESTADOS: Record<string, EstadoVisual> = {
  desconectado: { key: 'desconectado', label: 'Desconectado', color: '#6b7280', glow: 'rgba(107,114,128,0.35)', pulse: false },
  apagado:      { key: 'apagado',      label: 'Apagado',      color: '#475569', glow: 'rgba(71,85,105,0.30)',   pulse: false },
  intermitente: { key: 'intermitente', label: 'Intermitente', color: '#f59e0b', glow: 'rgba(245,158,11,0.45)', pulse: true },
  activo:       { key: 'activo',       label: 'Activo',       color: '#22c55e', glow: 'rgba(34,197,94,0.45)',  pulse: false },
  centralizado: { key: 'centralizado', label: 'Centralizado', color: '#06b6d4', glow: 'rgba(6,182,212,0.45)',  pulse: false },
  emergencia:   { key: 'emergencia',   label: 'Emergencia',   color: '#ef4444', glow: 'rgba(239,68,68,0.50)',  pulse: true },
  manual:       { key: 'manual',       label: 'Manual',       color: '#f97316', glow: 'rgba(249,115,22,0.45)', pulse: false },
};

export const ESTADO_DESCONECTADO = ESTADOS.desconectado;

/**
 * Resuelve la representación visual de un regulador.
 * @param estado - string del payload MQTT (puede ser un alias)
 * @param online - si es false, el regulador siempre se considera desconectado
 */
export function resolveEstado(estado: string | undefined, online: boolean): EstadoVisual {
  if (!online || !estado) return ESTADO_DESCONECTADO;
  const e = String(estado).toLowerCase();
  if (ESTADOS[e]) return ESTADOS[e];
  if (ALIAS_ACTIVO.has(e)) return ESTADOS.activo;
  return ESTADO_DESCONECTADO;
}
